import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type LotPolicy,
  type MarketThreshold,
  type ModelsSetting,
  type PriceSanity,
  type ExposureCaps,
  type SettingRow,
  type WeekendSchedule,
} from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ManagementPolicyEditor } from "@/components/ManagementPolicyEditor";
import { useTheme } from "@/hooks/useTheme";
import { pushToast } from "@/lib/toast";
import type { ThemeMode } from "@/lib/theme";

export function Settings() {
  const settings = useQuery({ queryKey: queryKeys.settings, queryFn: api.settings });
  const [theme, setTheme] = useTheme();

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>MetaApi accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-xs text-muted-foreground">
            Which broker account trades, what it subscribes to, deploy and undeploy.
          </p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/accounts">Manage accounts</Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="mb-1 block text-xs text-muted-foreground">Theme</label>
          <Select value={theme} onChange={(e) => setTheme(e.target.value as ThemeMode)}>
            <option value="system">System</option>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Everything is recorded and readable in the app. Choose which kinds also reach you.
          </p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/notifications/settings">What gets sent</Link>
          </Button>
          <TestNotificationButton />
        </CardContent>
      </Card>

      {settings.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : settings.isError ? (
        <p className="text-sm text-destructive">Could not load settings.</p>
      ) : (
        <>
          <BooleanSetting settingsKey="dry_run" label="Dry run" description="Simulate fills instead of placing real orders." />
          <LotPolicyCard />
          <MarketThresholdCard />
          <PriceSanityCard />
          <ExposureCapsCard />
          <NumberSetting settingsKey="parse_confidence_min" label="Parse confidence minimum" step="0.05" />
          <NumberSetting settingsKey="decide_confidence_min" label="Decide confidence minimum" step="0.05" />
          {/* How stale a signal may be before it is skipped instead of traded. Approving
              from the app is a human overriding it, so this bounds the automatic path only. */}
          <NumberSetting settingsKey="signal_max_age_minutes" label="Signal max age (minutes)" step="15" />
          {/* Raising this is how you resume parsing and deciding after the day's cap stops
              them; 0 turns the cap off entirely. */}
          <NumberSetting settingsKey="daily_llm_budget_usd" label="Daily model budget (USD)" step="1" />
          <ModelsCard />
          <CreditsCard />
          <WeekendScheduleCard />
          <GlobalRulesCard />
        </>
      )}
    </div>
  );
}

function useSettingRow(key: string) {
  const query = useQuery({ queryKey: queryKeys.settings, queryFn: api.settings });
  return { row: query.data?.settings.find((s: SettingRow) => s.key === key), isLoading: query.isLoading };
}

function usePutSetting(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown) => api.putSetting(key, value),
    onSuccess: (res) => {
      queryClient.setQueryData(queryKeys.settings, (old: { settings: SettingRow[] } | undefined) => {
        if (!old) return old;
        return { settings: old.settings.map((s) => (s.key === key ? { ...s, value: res.value, stored: true } : s)) };
      });
      pushToast(`${key.replace(/_/g, " ")} saved`);
    },
  });
}

function BooleanSetting({ settingsKey, label, description }: { settingsKey: string; label: string; description: string }) {
  const { row } = useSettingRow(settingsKey);
  const mutation = usePutSetting(settingsKey);
  if (!row) return null;

  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 p-4">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
        <Switch checked={row.value === true} disabled={mutation.isPending} onCheckedChange={(v) => mutation.mutate(v)} />
      </CardContent>
    </Card>
  );
}

function NumberSetting({ settingsKey, label, step = "1" }: { settingsKey: string; label: string; step?: string }) {
  const { row } = useSettingRow(settingsKey);
  const mutation = usePutSetting(settingsKey);
  const [text, setText] = useState("");
  const initialized = useRef(false);

  useEffect(() => {
    // Init-once: a background refetch (reconnect, remount) must not clobber an in-progress edit.
    if (row && !initialized.current) {
      setText(String(row.value));
      initialized.current = true;
    }
  }, [row]);

  if (!row) return null;
  const n = Number(text);
  const dirty = Number.isFinite(n) && n !== row.value;

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <label className="block text-xs text-muted-foreground">{label}</label>
        <div className="flex gap-2">
          <Input type="number" step={step} value={text} onChange={(e) => setText(e.target.value)} />
          <Button variant="outline" disabled={!dirty || mutation.isPending} onClick={() => mutation.mutate(n)}>
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * M15 — the two credit-monitoring settings in one card: warn/alarm thresholds (purely
 * informational markers) and the OpenAI baseline. "Set from dashboard figure" stamps the
 * moment, because remaining is computed as baseline minus spend SINCE that moment.
 *
 * M36d — the baseline half only appears when OpenAI is actually the provider. It exists
 * solely because OpenAI publishes no balance API; under OpenRouter the number is fetched,
 * and leaving the field on screen would invite Andy to type a figure nothing would read.
 * It is not deleted, because a rollback to the OpenAI arm brings it straight back.
 */
function CreditsCard() {
  const { row: thresholdsRow } = useSettingRow("credit_thresholds");
  const { row: baselineRow } = useSettingRow("openai_credit_baseline");
  const thresholdsMutation = usePutSetting("credit_thresholds");
  const baselineMutation = usePutSetting("openai_credit_baseline");

  const credits = useQuery({ queryKey: queryKeys.credits, queryFn: api.credits });

  const [thresholds, setThresholds] = useState<Record<string, string>>({});
  const [baselineText, setBaselineText] = useState("");
  const initialized = useRef(false);

  useEffect(() => {
    if (thresholdsRow && !initialized.current) {
      const value = thresholdsRow.value as Record<string, number>;
      setThresholds(Object.fromEntries(Object.entries(value).map(([k, v]) => [k, String(v)])));
      initialized.current = true;
    }
  }, [thresholdsRow]);

  if (!thresholdsRow || !baselineRow) return null;
  const baseline = baselineRow.value as { amountUsd: number | null; asOf: string | null };

  const FIELDS: { key: string; label: string }[] = [
    { key: "metaapiWarnUsd", label: "MetaApi warn ≤" },
    { key: "metaapiAlarmUsd", label: "MetaApi alarm ≤" },
    { key: "llmWarnUsd", label: "Models warn ≤" },
    { key: "llmAlarmUsd", label: "Models alarm ≤" },
  ];
  const parsed = Object.fromEntries(Object.entries(thresholds).map(([k, v]) => [k, Number(v)]));
  const valid = FIELDS.every((f) => Number.isFinite(parsed[f.key]) && parsed[f.key]! >= 0);

  const baselineAmount = Number(baselineText);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="mb-1 text-xs text-muted-foreground">
            Thresholds (USD) — mark the briefing and dashboard with ⚠/🚨. 0 disables.
          </div>
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="mb-1 block text-xs text-muted-foreground">{f.label}</label>
                <Input
                  type="number"
                  step="0.5"
                  value={thresholds[f.key] ?? ""}
                  onChange={(e) => setThresholds({ ...thresholds, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            className="mt-2"
            disabled={!valid || thresholdsMutation.isPending}
            onClick={() => thresholdsMutation.mutate(parsed)}
          >
            Save thresholds
          </Button>
        </div>

        {credits.data?.llm.provider === "openai" && (
        <div>
          <div className="mb-1 text-xs text-muted-foreground">
            OpenAI baseline — your dashboard balance right now. Remaining = this minus spend
            since. {baseline.amountUsd !== null && baseline.asOf
              ? `Currently $${baseline.amountUsd.toFixed(2)} as of ${baseline.asOf.slice(0, 10)}.`
              : "Not set — the dashboard shows a dash until it is."}
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              step="1"
              placeholder="e.g. 20"
              value={baselineText}
              onChange={(e) => setBaselineText(e.target.value)}
            />
            <Button
              variant="outline"
              disabled={!Number.isFinite(baselineAmount) || baselineText === "" || baselineMutation.isPending}
              onClick={() =>
                baselineMutation.mutate({ amountUsd: baselineAmount, asOf: new Date().toISOString() })
              }
            >
              Set from dashboard figure
            </Button>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}

function LotPolicyCard() {
  const { row } = useSettingRow("lot_policy");
  const mutation = usePutSetting("lot_policy");
  const [policy, setPolicy] = useState<LotPolicy | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && policy === null) setPolicy(row.value as LotPolicy);
  }, [row, policy]);

  if (!policy) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lot policy</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select
          value={policy.mode}
          onChange={(e) =>
            setPolicy(
              e.target.value === "fixed"
                ? { mode: "fixed", lotsPerOrder: 0.01 }
                : { mode: "risk_pct", riskPct: 1, maxLots: 1 },
            )
          }
        >
          <option value="fixed">Fixed lots per order</option>
          <option value="risk_pct">Risk percent of equity</option>
        </Select>

        {policy.mode === "fixed" ? (
          <Field label="Lots per order">
            <Input
              type="number"
              step="0.01"
              value={policy.lotsPerOrder}
              onChange={(e) => setPolicy({ mode: "fixed", lotsPerOrder: Number(e.target.value) })}
            />
          </Field>
        ) : (
          <>
            <Field label="Risk % of equity">
              <Input
                type="number"
                step="0.1"
                value={policy.riskPct}
                onChange={(e) => setPolicy({ ...policy, riskPct: Number(e.target.value) })}
              />
            </Field>
            <Field label="Max lots">
              <Input
                type="number"
                step="0.01"
                value={policy.maxLots}
                onChange={(e) => setPolicy({ ...policy, maxLots: Number(e.target.value) })}
              />
            </Field>
          </>
        )}

        <Button className="w-full" variant="outline" disabled={mutation.isPending} onClick={() => mutation.mutate(policy)}>
          {mutation.isPending ? "Saving…" : "Save lot policy"}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The market-vs-pending threshold, in broker pips, with per-symbol overrides.
 *
 * A single number cannot serve both: ten broker pips is ten cents on this broker's gold and
 * ten real pips on EURUSD. Same read-only treatment of the overrides map as price sanity —
 * editing the list from the phone is not worth the surface until there is a second entry.
 */
function MarketThresholdCard() {
  const { row } = useSettingRow("market_threshold_pips");
  const mutation = usePutSetting("market_threshold_pips");
  const [threshold, setThreshold] = useState<MarketThreshold | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && threshold === null) setThreshold(row.value as MarketThreshold);
  }, [row, threshold]);

  if (!threshold) return null;
  const overrideEntries = Object.entries(threshold.overrides);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Market threshold</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Default (pips)">
          <Input
            type="number"
            step="1"
            value={threshold.defaultPips}
            onChange={(e) => setThreshold({ ...threshold, defaultPips: Number(e.target.value) })}
          />
        </Field>
        <p className="text-xs text-muted-foreground">
          An entry within this many pips of the live quote is taken at market; anything further rests as a pending
          order.
        </p>
        {overrideEntries.length > 0 && (
          <div className="text-xs text-muted-foreground">
            Per-symbol overrides ({overrideEntries.map(([sym, pips]) => `${sym}: ${pips}`).join(", ")}) aren't editable
            from the app yet.
          </div>
        )}
        <Button
          className="w-full"
          variant="outline"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(threshold)}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

function PriceSanityCard() {
  const { row } = useSettingRow("price_sanity_pct");
  const mutation = usePutSetting("price_sanity_pct");
  const [sanity, setSanity] = useState<PriceSanity | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && sanity === null) setSanity(row.value as PriceSanity);
  }, [row, sanity]);

  if (!sanity) return null;
  const overrideEntries = Object.entries(sanity.overrides);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Price sanity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Default max deviation (%)">
          <Input
            type="number"
            step="0.1"
            value={sanity.defaultPct}
            onChange={(e) => setSanity({ ...sanity, defaultPct: Number(e.target.value) })}
          />
        </Field>
        {overrideEntries.length > 0 && (
          <div className="text-xs text-muted-foreground">
            Per-symbol overrides ({overrideEntries.map(([sym, pct]) => `${sym}: ${pct}%`).join(", ")}) aren't editable
            from the app yet.
          </div>
        )}
        <Button className="w-full" variant="outline" disabled={mutation.isPending} onClick={() => mutation.mutate(sanity)}>
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The account-wide caps from W1.9.
 *
 * These get a card rather than living behind `PUT /api/settings` alone because the setting's
 * own schema comment makes the argument: a limit you cannot raise from your phone at 3am is
 * one you will eventually route around. The zero-means-disabled behaviour is stated on screen
 * for the same reason — a cap silently switched off by a typed zero is worse than no cap.
 */
function ExposureCapsCard() {
  const { row } = useSettingRow("exposure_caps");
  const mutation = usePutSetting("exposure_caps");
  const [caps, setCaps] = useState<ExposureCaps | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && caps === null) setCaps(row.value as ExposureCaps);
  }, [row, caps]);

  if (!caps) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Exposure caps</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Max signals live at once">
          <Input
            type="number"
            step="1"
            value={caps.maxOpenSignals}
            onChange={(e) => setCaps({ ...caps, maxOpenSignals: Number(e.target.value) })}
          />
        </Field>
        <Field label="Max total risk (% of equity)">
          <Input
            type="number"
            step="0.5"
            value={caps.maxTotalRiskPct}
            onChange={(e) => setCaps({ ...caps, maxTotalRiskPct: Number(e.target.value) })}
          />
        </Field>
        <p className="text-xs text-muted-foreground">
          Risk is measured as money-to-stop across every live order. A new signal past either cap is refused and
          recorded with the reason. <strong>Zero disables that cap.</strong>
        </p>
        <Button className="w-full" variant="outline" disabled={mutation.isPending} onClick={() => mutation.mutate(caps)}>
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

function ModelsCard() {
  const { row } = useSettingRow("models");
  const mutation = usePutSetting("models");
  const [models, setModels] = useState<ModelsSetting | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && models === null) setModels(row.value as ModelsSetting);
  }, [row, models]);

  if (!models) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Models</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Parser model">
          <Input value={models.parser} onChange={(e) => setModels({ ...models, parser: e.target.value })} />
        </Field>
        <Field label="Decider model">
          <Input value={models.decider} onChange={(e) => setModels({ ...models, decider: e.target.value })} />
        </Field>
        <Field label="Decider reasoning effort">
          <Select
            value={models.deciderEffort}
            onChange={(e) => setModels({ ...models, deciderEffort: e.target.value as ModelsSetting["deciderEffort"] })}
          >
            {/* "None" sends no reasoning option at all — for a model that has no such
                parameter, which asking would refuse rather than merely ignore. */}
            <option value="none">None</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
        </Field>
        <Button className="w-full" variant="outline" disabled={mutation.isPending} onClick={() => mutation.mutate(models)}>
          {mutation.isPending ? "Saving…" : "Save models"}
        </Button>
      </CardContent>
    </Card>
  );
}

function WeekendScheduleCard() {
  const { row } = useSettingRow("weekend_schedule");
  const mutation = usePutSetting("weekend_schedule");
  const [schedule, setSchedule] = useState<WeekendSchedule | null>(null);

  useEffect(() => {
    // Init-once: a background refetch must not clobber an in-progress edit.
    if (row && schedule === null) setSchedule(row.value as WeekendSchedule);
  }, [row, schedule]);

  if (!schedule) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekend lifecycle</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">Enabled</span>
          <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })} />
        </div>
        <Field label="Undeploy cron (UTC)">
          <Input value={schedule.undeployCron} onChange={(e) => setSchedule({ ...schedule, undeployCron: e.target.value })} />
        </Field>
        <Field label="Redeploy cron (UTC)">
          <Input value={schedule.redeployCron} onChange={(e) => setSchedule({ ...schedule, redeployCron: e.target.value })} />
        </Field>
        <Button
          className="w-full"
          variant="outline"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(schedule)}
        >
          {mutation.isPending ? "Saving…" : "Save schedule"}
        </Button>
      </CardContent>
    </Card>
  );
}

function GlobalRulesCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How trades are managed</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          The default for every channel that has no settings of its own — a channel's Rules tab overrides it.
        </p>
        <ManagementPolicyEditor scope={{ kind: "global" }} />
      </CardContent>
    </Card>
  );
}

function TestNotificationButton() {
  const mutation = useMutation({
    mutationFn: api.notifyTest,
    onSuccess: () => pushToast("Test notification sent — check the system channel."),
    onError: (error) => pushToast(error instanceof Error ? error.message : "Test notification failed"),
  });
  return (
    <Button variant="outline" className="w-full" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
      {mutation.isPending ? "Sending…" : "Send test notification"}
    </Button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
